import { inject, Injectable } from "@angular/core";
import { first, map } from "rxjs";
import { GraphService } from "@kernel/graphql/services/graph.service";
import { GraphqlMessageInterface } from "@app/kernel/graphql/interfaces/graphql-message.interface";
import { EFilterCondition } from "@app/kernel/graphql/interfaces/query.interface";
import { FilterInput, FiltersGraphqlInput } from "@app/kernel/graphql/models";
import { ListingGraphqlInput } from "@app/kernel/graphql/models/listing.graphql.input";
import { PaginationGraphqlInput } from "@app/kernel/graphql/models/pagination.graphql.input";
import { IReservationSource } from "../interfaces/reservation-source.interface";
import { RESERVATION_SOURCE_FORM_QUERY } from "../queries/reservation-source-form.query";

/**
 * GraphQL operations for reservation sources. Provided on the domain route, not in root.
 * Create and update go through the form mutation configs.
 */
@Injectable()
export class ReservationSourceApiService {
  /** GraphQL service used for executing queries and mutations. */
  private readonly graphService = inject(GraphService);

  /**
   * Fetches a reservation source by id or entityId.
   *
   * @param id - The reservation source id or entityId.
   * @param dataShape - Selection set; defaults to the form query.
   * @returns An observable that emits the reservation source.
   * @throws GraphQL error if the response contains errors.
   */
  getReservationSource(id: string, dataShape: string | string[] = RESERVATION_SOURCE_FORM_QUERY) {
    return this.graphService.constructQuery<{ getReservationSource: IReservationSource }>(dataShape, "getReservationSource", { id }).pipe(
      first(),
      map(data => {
        if (data.errors?.length) {
          throw data.errors[0];
        }
        return data.data.getReservationSource;
      })
    );
  }

  /**
   * Counts the enabled Virtual RS, the only sources that can be a master. Reads one row: only the total is used.
   *
   * @returns An observable that emits the number of enabled Virtual RS.
   * @throws GraphQL error if the response contains errors.
   */
  countEnabledVirtualReservationSources() {
    const filters = new FiltersGraphqlInput(
      [new FilterInput("virtualRs", "IS", "true"), new FilterInput("enabled", "IS", "true")],
      EFilterCondition.AND
    );
    const listing = ListingGraphqlInput.wrap(filters, new PaginationGraphqlInput(1, 1), null, null);

    return this.graphService
      .constructListingQuery<{ listReservationSource: { pagination: { total: number } } }>(["id"], "listReservationSource", listing)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data.listReservationSource.pagination?.total ?? 0;
        })
      );
  }

  /**
   * Enables a reservation source.
   *
   * @param id - The reservation source id.
   * @param dataShape - Selection set; defaults to the form query.
   * @returns An observable that emits the enabled reservation source.
   * @throws GraphQL error if the response contains errors.
   */
  enableReservationSource(id: string, dataShape: string | string[] = RESERVATION_SOURCE_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{ enableReservationSource: IReservationSource }>("enableReservationSource", { id: "ID!" }, { id }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.enableReservationSource;
        })
      );
  }

  /**
   * Disables a reservation source.
   *
   * @param id - The reservation source id.
   * @param dataShape - Selection set; defaults to the form query.
   * @returns An observable that emits the disabled reservation source.
   * @throws GraphQL error if the response contains errors.
   */
  disableReservationSource(id: string, dataShape: string | string[] = RESERVATION_SOURCE_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{ disableReservationSource: IReservationSource }>("disableReservationSource", { id: "ID!" }, { id }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.disableReservationSource;
        })
      );
  }

  /**
   * Soft-deletes a reservation source.
   *
   * @param id - The reservation source id.
   * @returns An observable that emits the backend message.
   * @throws GraphQL error if the response contains errors.
   */
  deleteReservationSource(id: string) {
    return this.graphService
      .constructMutation<{
        deleteReservationSource: GraphqlMessageInterface;
      }>("deleteReservationSource", { id: "ID!" }, { id }, ["text", "code"])
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.deleteReservationSource;
        })
      );
  }
}
